import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "@/shared/lib/utils"
import { CONTROL, CONTROL_ICON, LIFT } from "@/shared/ui/law"

/**
 * Button — density from the token, depth from the law.
 *
 * Heights come from `--control-h` / `--control-h-sm` so the whole app
 * re-densifies from one knob (`data-density`); nothing here hard-codes a
 * control height.
 *
 * The filled variants are flat fills with a rung-one edge; hover darkens the
 * fill a step and `outline` firms its border (`LIFT`). Under graphite the
 * primary button is ink on paper, and that contrast is the whole affordance —
 * no sheen, no glow, no travel. `ghost` and `link` stay flat on purpose: they
 * are text that happens to be clickable.
 */
const buttonVariants = cva(
  cn(
    "inline-flex shrink-0 items-center justify-center gap-2 rounded-md text-sm font-medium whitespace-nowrap",
    CONTROL,
    CONTROL_ICON,
  ),
  {
    variants: {
      variant: {
        default: cn(
          "bg-primary text-primary-foreground shadow-1",
          "hover:bg-primary/90 active:bg-primary/85",
        ),
        destructive: cn(
          "bg-danger text-danger-foreground shadow-1 focus-visible:ring-danger",
          "hover:bg-danger/90 active:bg-danger/85",
        ),
        outline: cn(
          "border border-border bg-background text-foreground shadow-1",
          LIFT,
          "hover:border-border-strong hover:bg-accent hover:text-accent-foreground",
        ),
        secondary: cn(
          "bg-secondary text-secondary-foreground shadow-1",
          "hover:bg-secondary/80",
        ),
        // Text that happens to be clickable. No elevation, by design.
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-(--control-h) px-3 has-[>svg]:px-2.5",
        xs: "h-6 gap-1 rounded-md px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-(--control-h-sm) gap-1.5 rounded-md px-2.5 text-[13px] has-[>svg]:px-2",
        lg: "h-(--control-h-lg) rounded-md px-4 has-[>svg]:px-3",
        icon: "size-(--control-h)",
        "icon-xs": "size-6 rounded-md [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-(--control-h-sm)",
        "icon-lg": "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot.Root : "button"

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
